import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * GlobalMinerX i18n dictionary.
 * Supported languages: English (en), French (fr), Swahili (sw), Chinese (zh).
 *
 * Use `t('key')` from useTranslation() across the app.
 * Use `tStatus(key)` (export below) to translate backend enum values such as
 * "pending", "accepted", "in_progress", "verified", etc.
 */

const en = {
  // Navigation
  "nav.login": "Login",
  "nav.signup": "Sign Up",
  "nav.messages": "Messages",
  "nav.dashboard": "Dashboard",
  "nav.logout": "Logout",
  "nav.backHome": "Back to home",
  "nav.howItWorks": "How It Works",
  "nav.pricing": "Pricing & Fees",

  // Landing - Hero
  "hero.tagline": "Global Mineral Trading",
  "hero.title": "Global Mineral Trading",
  "hero.subtitle": "Simplified",
  "hero.description": "Connect with verified buyers and sellers of iron ore, gemstones, and minerals from Africa and beyond. Secure transactions with escrow protection.",
  "hero.getStarted": "Get Started",
  "hero.hotCommodities": "Hot Commodities",

  // Landing - Hot
  "hot.banner": "CRITICAL DEMAND",
  "hot.title": "Most Marketable Minerals",
  "hot.description": "Strategic minerals in high demand. List now for instant visibility and premium pricing.",
  "hot.use": "Use",
  "hot.viewListings": "View Listings",
  "hot.demand.critical": "CRITICAL",
  "hot.demand.veryHigh": "VERY HIGH",
  "hot.demand.high": "HIGH",
  "hot.use.electronics": "Electronics & Smartphones",
  "hot.use.infrastructure": "Infrastructure & EVs",
  "hot.use.evBatteries": "EV Batteries",
  "hot.use.batteryProduction": "Battery Production",
  "hot.use.steel": "Steel Production",
  "hot.use.smelting": "Smelting & Refining",

  // Landing - Featured
  "featured.title": "Featured Minerals",
  "featured.subtitle": "Discover trending and recently added minerals on the marketplace",
  "featured.empty": "No minerals listed yet. Be the first seller!",
  "featured.startSelling": "Start Selling",
  "featured.viewAll": "View All Minerals",
  "featured.verified": "Verified",
  "featured.docs": "Doc",
  "featured.docsPlural": "Docs",
  "featured.by": "By",

  // Landing - Features
  "features.title": "Why Choose GlobalMinerX",
  "features.subtitle": "The most trusted platform for international mineral trading",
  "features.escrow.title": "Secure Escrow",
  "features.escrow.desc": "Payments held safely until delivery confirmation. 3% commission ensures quality service.",
  "features.buyers.title": "Direct Buyers",
  "features.buyers.desc": "Connect directly with manufacturers and traders. No middlemen, better margins.",
  "features.insights.title": "Market Insights",
  "features.insights.desc": "See real-time demand data. Know what buyers want before you list.",

  // Landing - CTA
  "cta.title": "Ready to Start Trading?",
  "cta.description": "Join African mineral suppliers already connected with global buyers. List Coltan, Copper, Cobalt today.",
  "cta.button": "Join Now - It's Free",

  // Auth
  "auth.welcome": "Welcome Back",
  "auth.create": "Create Account",
  "auth.login": "Login to your account",
  "auth.join": "Join the mineral trading community",
  "auth.name": "Full Name",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.role": "I want to",
  "auth.buyer": "Buy Minerals",
  "auth.buyerDesc": "Browse and purchase minerals",
  "auth.seller": "Sell Minerals",
  "auth.sellerDesc": "List and sell your minerals",
  "auth.admin": "Admin",
  "auth.adminDesc": "Platform administration",
  "auth.loginBtn": "Login",
  "auth.createBtn": "Create Account",
  "auth.noAccount": "Don't have an account? Sign up",
  "auth.hasAccount": "Already have an account? Login",
  "auth.forgot": "Forgot your password?",
  "auth.toast.loginSuccess": "Login successful!",
  "auth.toast.verifyEmailHint": "Please verify your email to access all features",
  "auth.toast.regSuccess": "Registration successful! Please check your email.",
  "auth.toast.failed": "Authentication failed",
  "auth.resetTitle": "Reset Password",
  "auth.resetSubtitle": "Enter your email and we'll send you reset instructions.",
  "auth.newPassword": "New Password",
  "auth.confirmPassword": "Confirm Password",
  "auth.sendResetLink": "Send Reset Link",
  "auth.resetSuccess": "Password reset email sent. Check your inbox.",
  "auth.passwordsDontMatch": "Passwords don't match",
  "auth.passwordReset": "Password reset successful! Redirecting...",
  "auth.verifyTitle": "Verify Your Email",
  "auth.verifying": "Verifying email...",
  "auth.verifySuccess": "Email verified successfully!",
  "auth.verifyFail": "Verification failed",
  "auth.continueDashboard": "Continue to Dashboard",
  "auth.resendEmail": "Resend verification email",
  "auth.resendSent": "Verification email sent",

  // Common
  "common.loading": "Loading...",
  "common.search": "Search",
  "common.filter": "Filter",
  "common.browse": "Browse Minerals",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.submit": "Submit",
  "common.confirm": "Confirm",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.view": "View",
  "common.close": "Close",
  "common.back": "Back",
  "common.next": "Next",
  "common.yes": "Yes",
  "common.no": "No",
  "common.price": "Price",
  "common.quantity": "Quantity",
  "common.location": "Location",
  "common.category": "Category",
  "common.grade": "Grade/Quality",
  "common.actions": "Actions",
  "common.status": "Status",
  "common.date": "Date",
  "common.all": "All",
  "common.none": "None",
  "common.export": "Export",
  "common.refresh": "Refresh",
  "common.success": "Success",
  "common.error": "Error",
  "common.unverified": "Unverified",
  "common.verified": "Verified",
  "common.pending": "Pending",

  // Minerals
  "minerals.coltan": "Coltan (Tantalite)",
  "minerals.copper": "Copper Concentrate",
  "minerals.copperCathode": "Copper Cathode",
  "minerals.cobalt": "Cobalt Ore",
  "minerals.lithium": "Lithium Ore",
  "minerals.manganese": "Manganese",
  "minerals.chromium": "Chromium",
  "minerals.gold": "Gold",
  "minerals.tin": "Tin",
  "minerals.tungsten": "Tungsten",
  "minerals.iron": "Iron Ore",
  "minerals.diamond": "Diamond",
  "minerals.gemstone": "Gemstone",
  "minerals.other": "Other",

  // Buyer Dashboard
  "buyer.title": "Buyer Dashboard",
  "buyer.subtitle": "Discover quality minerals and track your deals",
  "buyer.tabs.marketplace": "Marketplace",
  "buyer.tabs.myOffers": "My Offers",
  "buyer.tabs.myDeals": "My Deals",
  "buyer.searchPlaceholder": "Search minerals, locations...",
  "buyer.filterCategory": "Category",
  "buyer.allCategories": "All Categories",
  "buyer.empty.minerals": "No minerals found. Try changing filters.",
  "buyer.empty.offers": "You haven't made any offers yet.",
  "buyer.empty.deals": "No deals yet.",
  "buyer.makeOffer": "Make Offer",
  "buyer.viewDetails": "View Details",
  "buyer.cancelOffer": "Cancel Offer",
  "buyer.cancelOfferConfirm": "Cancel this offer? This cannot be undone.",
  "buyer.offerCancelled": "Offer cancelled",
  "buyer.viewSummary": "View Deal Summary",
  "buyer.offerAmount": "Your Offer",
  "buyer.targetPrice": "Listed Price",
  "buyer.deal.id": "Deal ID",
  "buyer.deal.agreed": "Agreed Price",
  "buyer.deal.qty": "Quantity",
  "buyer.deal.status": "Status",

  // Seller Dashboard
  "seller.title": "Seller Dashboard",
  "seller.subtitle": "Manage listings, offers, and deals",
  "seller.tabs.listings": "My Listings",
  "seller.tabs.offers": "Received Offers",
  "seller.tabs.deals": "My Deals",
  "seller.createListing": "Create Listing",
  "seller.empty.listings": "No listings yet. Start by creating one.",
  "seller.empty.offers": "No offers received yet.",
  "seller.empty.deals": "No active deals yet.",
  "seller.acceptOffer": "Accept",
  "seller.counter": "Counter",
  "seller.reject": "Reject",
  "seller.deleteListing": "Delete Listing",
  "seller.deleteConfirm": "Delete this listing? This cannot be undone.",
  "seller.listingDeleted": "Listing deleted",
  "seller.offerAccepted": "Offer accepted",
  "seller.offerRejected": "Offer rejected",
  "seller.unverifiedNotice": "Your listings will be marked Pending Verification until reviewed by admin.",

  // Create Listing
  "create.title": "Create Mineral Listing",
  "create.subtitle": "Provide accurate details to attract verified buyers",
  "create.field.title": "Title",
  "create.field.description": "Description",
  "create.field.category": "Category",
  "create.field.grade": "Grade / Quality",
  "create.field.price": "Price (USD)",
  "create.field.unit": "Unit",
  "create.field.quantity": "Quantity Available",
  "create.field.location": "Location",
  "create.field.images": "Upload Images",
  "create.submit": "Publish Listing",
  "create.success": "Listing created successfully",
  "create.fail": "Failed to create listing",

  // Mineral Detail
  "detail.makeOffer": "Make Offer",
  "detail.contactSeller": "Contact Seller",
  "detail.specs": "Specifications",
  "detail.description": "Description",
  "detail.seller": "Seller",
  "detail.location": "Location",
  "detail.listed": "Listed",
  "detail.views": "Views",
  "detail.offerPlaceholder": "Your offer price (USD)",
  "detail.offerQuantity": "Quantity (kg)",
  "detail.offerMessage": "Message to seller (optional)",
  "detail.submitOffer": "Submit Offer",
  "detail.offerSent": "Offer sent successfully",
  "detail.unverifiedSeller": "Unverified Seller",
  "detail.verifiedSeller": "Verified Seller",
  "detail.pendingApproval": "Pending Verification",

  // Messages
  "messages.title": "Messages",
  "messages.empty": "No messages yet.",
  "messages.placeholder": "Type a message...",
  "messages.send": "Send",

  // Inspection
  "inspection.title": "Inspection",
  "inspection.scheduleTitle": "Schedule Inspection",
  "inspection.subtitle": "Coordinate inspection of the mineral lot before payment release",
  "inspection.date": "Inspection Date",
  "inspection.notes": "Notes",
  "inspection.submit": "Schedule Inspection",
  "inspection.scheduled": "Inspection scheduled",

  // Documents
  "doc.title": "Document Upload",
  "doc.subtitle": "Upload certificates, assays, and proofs to verify your listing",
  "doc.upload": "Upload Document",
  "doc.type": "Document Type",
  "doc.success": "Document uploaded",

  // Admin
  "admin.title": "Admin Dashboard",
  "admin.subtitle": "Platform governance, users, listings & deals",
  "admin.tabs.overview": "Overview",
  "admin.tabs.users": "Users",
  "admin.tabs.listings": "Listings",
  "admin.tabs.deals": "Deals",
  "admin.tabs.compliance": "Compliance",
  "admin.tabs.audit": "Audit Logs",
  "admin.stats.users": "Total Users",
  "admin.stats.listings": "Active Listings",
  "admin.stats.deals": "Active Deals",
  "admin.stats.gmv": "Gross Volume",
  "admin.users.deleteConfirm": "Permanently delete this user?",
  "admin.users.deleted": "User deleted",
  "admin.listings.approve": "Approve",
  "admin.listings.reject": "Reject",
  "admin.listings.approved": "Listing approved",
  "admin.listings.rejected": "Listing rejected",
  "admin.listings.delete": "Delete",
  "admin.listings.deleteConfirm": "Force delete this listing?",
  "admin.deals.verify": "Verify Deal",
  "admin.deals.verified": "Deal verified and moved to in progress",

  // Market Insights
  "insights.title": "Market Insights",
  "insights.subtitle": "Live demand and trends across African minerals",

  // How It Works
  "how.title": "How GlobalMinerX Works",
  "how.subtitle": "From listing to delivery — your journey explained",

  // Footer
  "footer.company": "Connecting African mineral suppliers with global buyers. Trade Coltan, Copper, Cobalt, and more.",
  "footer.quickLinks": "Quick Links",
  "footer.popularMinerals": "Popular Minerals",
  "footer.connect": "Connect With Us",
  "footer.rights": "Connecting Africa to Global Buyers. All rights reserved.",
  "footer.privacy": "Privacy Policy",
  "footer.terms": "Terms of Service",

  // Status enums (offer / deal / listing)
  "status.pending": "Pending",
  "status.accepted": "Accepted",
  "status.rejected": "Rejected",
  "status.countered": "Countered",
  "status.cancelled": "Cancelled",
  "status.expired": "Expired",
  "status.in_progress": "In Progress",
  "status.completed": "Completed",
  "status.disputed": "Disputed",
  "status.verified": "Verified",
  "status.unverified": "Unverified",
  "status.pending_verification": "Pending Verification",
  "status.approved": "Approved",
  "status.under_review": "Under Review",
  "status.escalated": "Escalated",
  "status.active": "Active",
  "status.inactive": "Inactive",
  "status.suspended": "Suspended",
  "status.draft": "Draft",
  "status.scheduled": "Scheduled",
  "status.in_inspection": "In Inspection",
  "status.payment_pending": "Payment Pending",
  "status.shipped": "Shipped",
  "status.delivered": "Delivered",

  // Error / generic
  "error.generic": "Something went wrong",
  "error.network": "Network error. Please retry.",
  "error.unauthorized": "Unauthorized. Please log in again.",
};

const fr = {
  // Navigation
  "nav.login": "Connexion",
  "nav.signup": "S'inscrire",
  "nav.messages": "Messages",
  "nav.dashboard": "Tableau de bord",
  "nav.logout": "Déconnexion",
  "nav.backHome": "Retour à l'accueil",
  "nav.howItWorks": "Comment ça marche",
  "nav.pricing": "Tarifs et frais",

  "hero.tagline": "Commerce mondial de minéraux",
  "hero.title": "Commerce Mondial de Minéraux",
  "hero.subtitle": "Simplifié",
  "hero.description": "Connectez-vous avec des acheteurs et vendeurs vérifiés de minerai de fer, pierres précieuses et minéraux d'Afrique et d'ailleurs. Transactions sécurisées avec protection séquestre.",
  "hero.getStarted": "Commencer",
  "hero.hotCommodities": "Produits Populaires",

  "hot.banner": "DEMANDE CRITIQUE",
  "hot.title": "Minéraux Les Plus Commercialisables",
  "hot.description": "Minéraux stratégiques en forte demande. Listez maintenant pour une visibilité instantanée et des prix premium.",
  "hot.use": "Utilisation",
  "hot.viewListings": "Voir les annonces",
  "hot.demand.critical": "CRITIQUE",
  "hot.demand.veryHigh": "TRÈS ÉLEVÉE",
  "hot.demand.high": "ÉLEVÉE",
  "hot.use.electronics": "Électronique et Smartphones",
  "hot.use.infrastructure": "Infrastructures et VE",
  "hot.use.evBatteries": "Batteries de VE",
  "hot.use.batteryProduction": "Production de batteries",
  "hot.use.steel": "Production d'acier",
  "hot.use.smelting": "Fonte et raffinage",

  "featured.title": "Minéraux en Vedette",
  "featured.subtitle": "Découvrez les minéraux tendance et récemment ajoutés sur la marketplace",
  "featured.empty": "Aucun minéral listé pour le moment. Soyez le premier vendeur !",
  "featured.startSelling": "Commencer à vendre",
  "featured.viewAll": "Voir tous les minéraux",
  "featured.verified": "Vérifié",
  "featured.docs": "Doc",
  "featured.docsPlural": "Docs",
  "featured.by": "Par",

  "features.title": "Pourquoi Choisir GlobalMinerX",
  "features.subtitle": "La plateforme la plus fiable pour le commerce international de minéraux",
  "features.escrow.title": "Séquestre Sécurisé",
  "features.escrow.desc": "Paiements conservés en toute sécurité jusqu'à confirmation de livraison. Commission de 3% garantit un service de qualité.",
  "features.buyers.title": "Acheteurs Directs",
  "features.buyers.desc": "Connectez-vous directement avec les fabricants et commerçants. Pas d'intermédiaires, meilleures marges.",
  "features.insights.title": "Aperçus du Marché",
  "features.insights.desc": "Consultez les données de demande en temps réel. Sachez ce que les acheteurs veulent avant de lister.",

  "cta.title": "Prêt à Commencer le Commerce ?",
  "cta.description": "Rejoignez les fournisseurs africains déjà connectés à des acheteurs mondiaux. Listez Coltan, Cuivre, Cobalt aujourd'hui.",
  "cta.button": "Rejoindre - C'est gratuit",

  "auth.welcome": "Bon Retour",
  "auth.create": "Créer un Compte",
  "auth.login": "Connectez-vous à votre compte",
  "auth.join": "Rejoignez la communauté du commerce de minéraux",
  "auth.name": "Nom Complet",
  "auth.email": "Email",
  "auth.password": "Mot de passe",
  "auth.role": "Je veux",
  "auth.buyer": "Acheter des Minéraux",
  "auth.buyerDesc": "Parcourir et acheter des minéraux",
  "auth.seller": "Vendre des Minéraux",
  "auth.sellerDesc": "Lister et vendre vos minéraux",
  "auth.admin": "Administrateur",
  "auth.adminDesc": "Administration de la plateforme",
  "auth.loginBtn": "Connexion",
  "auth.createBtn": "Créer un Compte",
  "auth.noAccount": "Vous n'avez pas de compte ? Inscrivez-vous",
  "auth.hasAccount": "Vous avez déjà un compte ? Connexion",
  "auth.forgot": "Mot de passe oublié ?",
  "auth.toast.loginSuccess": "Connexion réussie !",
  "auth.toast.verifyEmailHint": "Veuillez vérifier votre email pour accéder à toutes les fonctionnalités",
  "auth.toast.regSuccess": "Inscription réussie ! Veuillez vérifier votre email.",
  "auth.toast.failed": "Échec de l'authentification",
  "auth.resetTitle": "Réinitialiser le mot de passe",
  "auth.resetSubtitle": "Entrez votre email et nous vous enverrons les instructions.",
  "auth.newPassword": "Nouveau mot de passe",
  "auth.confirmPassword": "Confirmer le mot de passe",
  "auth.sendResetLink": "Envoyer le lien",
  "auth.resetSuccess": "Email de réinitialisation envoyé. Vérifiez votre boîte de réception.",
  "auth.passwordsDontMatch": "Les mots de passe ne correspondent pas",
  "auth.passwordReset": "Mot de passe réinitialisé ! Redirection...",
  "auth.verifyTitle": "Vérifiez votre email",
  "auth.verifying": "Vérification de l'email...",
  "auth.verifySuccess": "Email vérifié avec succès !",
  "auth.verifyFail": "Échec de la vérification",
  "auth.continueDashboard": "Continuer vers le tableau de bord",
  "auth.resendEmail": "Renvoyer l'email de vérification",
  "auth.resendSent": "Email de vérification envoyé",

  "common.loading": "Chargement...",
  "common.search": "Rechercher",
  "common.filter": "Filtrer",
  "common.browse": "Parcourir les Minéraux",
  "common.cancel": "Annuler",
  "common.save": "Enregistrer",
  "common.submit": "Soumettre",
  "common.confirm": "Confirmer",
  "common.delete": "Supprimer",
  "common.edit": "Modifier",
  "common.view": "Voir",
  "common.close": "Fermer",
  "common.back": "Retour",
  "common.next": "Suivant",
  "common.yes": "Oui",
  "common.no": "Non",
  "common.price": "Prix",
  "common.quantity": "Quantité",
  "common.location": "Emplacement",
  "common.category": "Catégorie",
  "common.grade": "Grade/Qualité",
  "common.actions": "Actions",
  "common.status": "Statut",
  "common.date": "Date",
  "common.all": "Tous",
  "common.none": "Aucun",
  "common.export": "Exporter",
  "common.refresh": "Actualiser",
  "common.success": "Succès",
  "common.error": "Erreur",
  "common.unverified": "Non vérifié",
  "common.verified": "Vérifié",
  "common.pending": "En attente",

  "minerals.coltan": "Coltan (Tantalite)",
  "minerals.copper": "Concentré de Cuivre",
  "minerals.copperCathode": "Cathode de Cuivre",
  "minerals.cobalt": "Minerai de Cobalt",
  "minerals.lithium": "Minerai de Lithium",
  "minerals.manganese": "Manganèse",
  "minerals.chromium": "Chrome",
  "minerals.gold": "Or",
  "minerals.tin": "Étain",
  "minerals.tungsten": "Tungstène",
  "minerals.iron": "Minerai de fer",
  "minerals.diamond": "Diamant",
  "minerals.gemstone": "Pierre précieuse",
  "minerals.other": "Autre",

  "buyer.title": "Tableau de bord Acheteur",
  "buyer.subtitle": "Découvrez des minéraux de qualité et suivez vos transactions",
  "buyer.tabs.marketplace": "Marketplace",
  "buyer.tabs.myOffers": "Mes Offres",
  "buyer.tabs.myDeals": "Mes Transactions",
  "buyer.searchPlaceholder": "Rechercher minéraux, lieux...",
  "buyer.filterCategory": "Catégorie",
  "buyer.allCategories": "Toutes les catégories",
  "buyer.empty.minerals": "Aucun minéral trouvé. Modifiez les filtres.",
  "buyer.empty.offers": "Vous n'avez encore fait aucune offre.",
  "buyer.empty.deals": "Aucune transaction pour le moment.",
  "buyer.makeOffer": "Faire une offre",
  "buyer.viewDetails": "Voir les détails",
  "buyer.cancelOffer": "Annuler l'offre",
  "buyer.cancelOfferConfirm": "Annuler cette offre ? Cette action est irréversible.",
  "buyer.offerCancelled": "Offre annulée",
  "buyer.viewSummary": "Voir le résumé",
  "buyer.offerAmount": "Votre offre",
  "buyer.targetPrice": "Prix listé",
  "buyer.deal.id": "ID de la transaction",
  "buyer.deal.agreed": "Prix convenu",
  "buyer.deal.qty": "Quantité",
  "buyer.deal.status": "Statut",

  "seller.title": "Tableau de bord Vendeur",
  "seller.subtitle": "Gérez les annonces, offres et transactions",
  "seller.tabs.listings": "Mes Annonces",
  "seller.tabs.offers": "Offres reçues",
  "seller.tabs.deals": "Mes Transactions",
  "seller.createListing": "Créer une annonce",
  "seller.empty.listings": "Aucune annonce. Commencez par en créer une.",
  "seller.empty.offers": "Aucune offre reçue.",
  "seller.empty.deals": "Aucune transaction active.",
  "seller.acceptOffer": "Accepter",
  "seller.counter": "Contre-offre",
  "seller.reject": "Refuser",
  "seller.deleteListing": "Supprimer l'annonce",
  "seller.deleteConfirm": "Supprimer cette annonce ? Action irréversible.",
  "seller.listingDeleted": "Annonce supprimée",
  "seller.offerAccepted": "Offre acceptée",
  "seller.offerRejected": "Offre refusée",
  "seller.unverifiedNotice": "Vos annonces seront marquées En attente de vérification jusqu'à examen par l'admin.",

  "create.title": "Créer une annonce de minéral",
  "create.subtitle": "Fournissez des détails précis pour attirer des acheteurs vérifiés",
  "create.field.title": "Titre",
  "create.field.description": "Description",
  "create.field.category": "Catégorie",
  "create.field.grade": "Grade / Qualité",
  "create.field.price": "Prix (USD)",
  "create.field.unit": "Unité",
  "create.field.quantity": "Quantité disponible",
  "create.field.location": "Emplacement",
  "create.field.images": "Téléverser des images",
  "create.submit": "Publier l'annonce",
  "create.success": "Annonce créée avec succès",
  "create.fail": "Échec de la création",

  "detail.makeOffer": "Faire une offre",
  "detail.contactSeller": "Contacter le vendeur",
  "detail.specs": "Spécifications",
  "detail.description": "Description",
  "detail.seller": "Vendeur",
  "detail.location": "Emplacement",
  "detail.listed": "Listé",
  "detail.views": "Vues",
  "detail.offerPlaceholder": "Votre prix d'offre (USD)",
  "detail.offerQuantity": "Quantité (kg)",
  "detail.offerMessage": "Message au vendeur (optionnel)",
  "detail.submitOffer": "Soumettre l'offre",
  "detail.offerSent": "Offre envoyée avec succès",
  "detail.unverifiedSeller": "Vendeur non vérifié",
  "detail.verifiedSeller": "Vendeur vérifié",
  "detail.pendingApproval": "En attente de vérification",

  "messages.title": "Messages",
  "messages.empty": "Aucun message.",
  "messages.placeholder": "Écrire un message...",
  "messages.send": "Envoyer",

  "inspection.title": "Inspection",
  "inspection.scheduleTitle": "Planifier une inspection",
  "inspection.subtitle": "Coordonnez l'inspection du lot avant la libération du paiement",
  "inspection.date": "Date d'inspection",
  "inspection.notes": "Notes",
  "inspection.submit": "Planifier l'inspection",
  "inspection.scheduled": "Inspection planifiée",

  "doc.title": "Téléversement de documents",
  "doc.subtitle": "Téléversez certificats, analyses et preuves pour vérifier votre annonce",
  "doc.upload": "Téléverser un document",
  "doc.type": "Type de document",
  "doc.success": "Document téléversé",

  "admin.title": "Tableau de bord Admin",
  "admin.subtitle": "Gouvernance plateforme, utilisateurs, annonces & transactions",
  "admin.tabs.overview": "Aperçu",
  "admin.tabs.users": "Utilisateurs",
  "admin.tabs.listings": "Annonces",
  "admin.tabs.deals": "Transactions",
  "admin.tabs.compliance": "Conformité",
  "admin.tabs.audit": "Journaux d'audit",
  "admin.stats.users": "Utilisateurs totaux",
  "admin.stats.listings": "Annonces actives",
  "admin.stats.deals": "Transactions actives",
  "admin.stats.gmv": "Volume brut",
  "admin.users.deleteConfirm": "Supprimer définitivement cet utilisateur ?",
  "admin.users.deleted": "Utilisateur supprimé",
  "admin.listings.approve": "Approuver",
  "admin.listings.reject": "Rejeter",
  "admin.listings.approved": "Annonce approuvée",
  "admin.listings.rejected": "Annonce rejetée",
  "admin.listings.delete": "Supprimer",
  "admin.listings.deleteConfirm": "Forcer la suppression de cette annonce ?",
  "admin.deals.verify": "Vérifier la transaction",
  "admin.deals.verified": "Transaction vérifiée et passée en cours",

  "insights.title": "Aperçus du marché",
  "insights.subtitle": "Demande et tendances en direct sur les minéraux africains",

  "how.title": "Comment fonctionne GlobalMinerX",
  "how.subtitle": "De l'annonce à la livraison — votre parcours expliqué",

  "footer.company": "Connecter les fournisseurs africains de minéraux avec des acheteurs mondiaux. Échangez Coltan, Cuivre, Cobalt et plus.",
  "footer.quickLinks": "Liens Rapides",
  "footer.popularMinerals": "Minéraux Populaires",
  "footer.connect": "Connectez-vous avec Nous",
  "footer.rights": "Connecter l'Afrique aux Acheteurs Mondiaux. Tous droits réservés.",
  "footer.privacy": "Politique de confidentialité",
  "footer.terms": "Conditions d'utilisation",

  "status.pending": "En attente",
  "status.accepted": "Accepté",
  "status.rejected": "Refusé",
  "status.countered": "Contre-offre",
  "status.cancelled": "Annulé",
  "status.expired": "Expiré",
  "status.in_progress": "En cours",
  "status.completed": "Terminé",
  "status.disputed": "En litige",
  "status.verified": "Vérifié",
  "status.unverified": "Non vérifié",
  "status.pending_verification": "En attente de vérification",
  "status.approved": "Approuvé",
  "status.under_review": "En cours d'examen",
  "status.escalated": "Escaladé",
  "status.active": "Actif",
  "status.inactive": "Inactif",
  "status.suspended": "Suspendu",
  "status.draft": "Brouillon",
  "status.scheduled": "Planifié",
  "status.in_inspection": "En inspection",
  "status.payment_pending": "Paiement en attente",
  "status.shipped": "Expédié",
  "status.delivered": "Livré",

  "error.generic": "Une erreur est survenue",
  "error.network": "Erreur réseau. Veuillez réessayer.",
  "error.unauthorized": "Non autorisé. Veuillez vous reconnecter.",
};

const sw = {
  "nav.login": "Ingia",
  "nav.signup": "Jisajili",
  "nav.messages": "Ujumbe",
  "nav.dashboard": "Dashibodi",
  "nav.logout": "Toka",
  "nav.backHome": "Rudi nyumbani",
  "nav.howItWorks": "Inavyofanya kazi",
  "nav.pricing": "Bei na Ada",

  "hero.tagline": "Biashara ya madini duniani",
  "hero.title": "Biashara ya Madini Duniani",
  "hero.subtitle": "Imerahisishwa",
  "hero.description": "Unganisha na wanunuzi na wauzaji walioidhinishwa wa chuma, vito, na madini kutoka Afrika na zaidi. Miamala salama na ulinzi wa escrow.",
  "hero.getStarted": "Anza",
  "hero.hotCommodities": "Bidhaa Zinazotakiwa Sana",

  "hot.banner": "MAHITAJI MAKUBWA",
  "hot.title": "Madini Yenye Soko Kubwa",
  "hot.description": "Madini muhimu yenye mahitaji makubwa. Orodhesha sasa kwa mwonekano wa papo hapo na bei bora.",
  "hot.use": "Matumizi",
  "hot.viewListings": "Tazama orodha",
  "hot.demand.critical": "MUHIMU SANA",
  "hot.demand.veryHigh": "JUU SANA",
  "hot.demand.high": "JUU",
  "hot.use.electronics": "Elektroniki na Simu",
  "hot.use.infrastructure": "Miundombinu na Magari ya Umeme",
  "hot.use.evBatteries": "Betri za Magari ya Umeme",
  "hot.use.batteryProduction": "Utengenezaji wa Betri",
  "hot.use.steel": "Utengenezaji wa Chuma",
  "hot.use.smelting": "Uyeyushaji na Usafishaji",

  "featured.title": "Madini Maarufu",
  "featured.subtitle": "Gundua madini yanayotamba na yaliyoongezwa hivi karibuni",
  "featured.empty": "Hakuna madini bado. Kuwa muuzaji wa kwanza!",
  "featured.startSelling": "Anza kuuza",
  "featured.viewAll": "Tazama madini yote",
  "featured.verified": "Imethibitishwa",
  "featured.docs": "Hati",
  "featured.docsPlural": "Hati",
  "featured.by": "Na",

  "features.title": "Kwa Nini Chagua GlobalMinerX",
  "features.subtitle": "Jukwaa linaloweza kuaminika zaidi kwa biashara ya madini ya kimataifa",
  "features.escrow.title": "Escrow Salama",
  "features.escrow.desc": "Malipo yanawekwa salama hadi uthibitisho wa utoaji. Toza ya 3% inahakikisha huduma bora.",
  "features.buyers.title": "Wanunuzi wa Moja kwa Moja",
  "features.buyers.desc": "Unganisha moja kwa moja na watengenezaji na wafanyabiashara. Hakuna wapatanishi, faida bora.",
  "features.insights.title": "Maarifa ya Soko",
  "features.insights.desc": "Tazama data ya mahitaji ya wakati halisi. Jua wanunuzi wanataka nini kabla ya kuorodhesha.",

  "cta.title": "Tayari Kuanza Biashara?",
  "cta.description": "Jiunge na watoaji wa Afrika tayari wameunganishwa na wanunuzi wa kimataifa. Orodhesha Coltan, Shaba, Cobalt leo.",
  "cta.button": "Jiunge sasa - Ni bure",

  "auth.welcome": "Karibu Tena",
  "auth.create": "Fungua Akaunti",
  "auth.login": "Ingia kwenye akaunti yako",
  "auth.join": "Jiunge na jumuiya ya biashara ya madini",
  "auth.name": "Jina Kamili",
  "auth.email": "Barua pepe",
  "auth.password": "Nenosiri",
  "auth.role": "Nataka",
  "auth.buyer": "Kununua Madini",
  "auth.buyerDesc": "Vinjari na ununue madini",
  "auth.seller": "Kuuza Madini",
  "auth.sellerDesc": "Orodhesha na uuze madini yako",
  "auth.admin": "Msimamizi",
  "auth.adminDesc": "Usimamizi wa jukwaa",
  "auth.loginBtn": "Ingia",
  "auth.createBtn": "Fungua Akaunti",
  "auth.noAccount": "Huna akaunti? Jisajili",
  "auth.hasAccount": "Tayari una akaunti? Ingia",
  "auth.forgot": "Umesahau nenosiri?",
  "auth.toast.loginSuccess": "Umeingia kwa mafanikio!",
  "auth.toast.verifyEmailHint": "Tafadhali thibitisha barua pepe yako kupata huduma zote",
  "auth.toast.regSuccess": "Usajili umefanikiwa! Angalia barua pepe yako.",
  "auth.toast.failed": "Uthibitishaji umeshindikana",
  "auth.resetTitle": "Weka Upya Nenosiri",
  "auth.resetSubtitle": "Weka barua pepe yako na tutakutumia maelekezo.",
  "auth.newPassword": "Nenosiri Jipya",
  "auth.confirmPassword": "Thibitisha Nenosiri",
  "auth.sendResetLink": "Tuma Kiungo",
  "auth.resetSuccess": "Barua pepe ya kuweka upya imetumwa.",
  "auth.passwordsDontMatch": "Nenosiri halifanani",
  "auth.passwordReset": "Nenosiri limewekwa upya! Inaelekeza...",
  "auth.verifyTitle": "Thibitisha Barua Pepe",
  "auth.verifying": "Inathibitisha...",
  "auth.verifySuccess": "Barua pepe imethibitishwa!",
  "auth.verifyFail": "Uthibitishaji umeshindikana",
  "auth.continueDashboard": "Endelea kwenye Dashibodi",
  "auth.resendEmail": "Tuma tena barua pepe ya uthibitisho",
  "auth.resendSent": "Barua pepe ya uthibitisho imetumwa",

  "common.loading": "Inapakia...",
  "common.search": "Tafuta",
  "common.filter": "Chuja",
  "common.browse": "Vinjari Madini",
  "common.cancel": "Ghairi",
  "common.save": "Hifadhi",
  "common.submit": "Wasilisha",
  "common.confirm": "Thibitisha",
  "common.delete": "Futa",
  "common.edit": "Hariri",
  "common.view": "Tazama",
  "common.close": "Funga",
  "common.back": "Rudi",
  "common.next": "Endelea",
  "common.yes": "Ndiyo",
  "common.no": "Hapana",
  "common.price": "Bei",
  "common.quantity": "Kiasi",
  "common.location": "Mahali",
  "common.category": "Aina",
  "common.grade": "Daraja/Ubora",
  "common.actions": "Vitendo",
  "common.status": "Hali",
  "common.date": "Tarehe",
  "common.all": "Yote",
  "common.none": "Hakuna",
  "common.export": "Hamisha",
  "common.refresh": "Onyesha upya",
  "common.success": "Mafanikio",
  "common.error": "Hitilafu",
  "common.unverified": "Hayajathibitishwa",
  "common.verified": "Yamethibitishwa",
  "common.pending": "Inasubiri",

  "minerals.coltan": "Coltan (Tantalite)",
  "minerals.copper": "Mkusanyiko wa Shaba",
  "minerals.copperCathode": "Cathode ya Shaba",
  "minerals.cobalt": "Madini ya Cobalt",
  "minerals.lithium": "Madini ya Lithium",
  "minerals.manganese": "Manganese",
  "minerals.chromium": "Chromium",
  "minerals.gold": "Dhahabu",
  "minerals.tin": "Bati",
  "minerals.tungsten": "Tungsten",
  "minerals.iron": "Madini ya Chuma",
  "minerals.diamond": "Almasi",
  "minerals.gemstone": "Vito",
  "minerals.other": "Nyingine",

  "buyer.title": "Dashibodi ya Mnunuzi",
  "buyer.subtitle": "Gundua madini bora na fuatilia miamala yako",
  "buyer.tabs.marketplace": "Soko",
  "buyer.tabs.myOffers": "Toleo Zangu",
  "buyer.tabs.myDeals": "Miamala Yangu",
  "buyer.searchPlaceholder": "Tafuta madini, maeneo...",
  "buyer.filterCategory": "Aina",
  "buyer.allCategories": "Aina zote",
  "buyer.empty.minerals": "Hakuna madini. Badilisha vichujio.",
  "buyer.empty.offers": "Bado hujatoa toleo lolote.",
  "buyer.empty.deals": "Hakuna miamala bado.",
  "buyer.makeOffer": "Toa Toleo",
  "buyer.viewDetails": "Tazama Maelezo",
  "buyer.cancelOffer": "Ghairi Toleo",
  "buyer.cancelOfferConfirm": "Ghairi toleo hili? Haliwezi kurejeshwa.",
  "buyer.offerCancelled": "Toleo limeghairiwa",
  "buyer.viewSummary": "Tazama Muhtasari",
  "buyer.offerAmount": "Toleo Lako",
  "buyer.targetPrice": "Bei Iliyotangazwa",
  "buyer.deal.id": "Kitambulisho",
  "buyer.deal.agreed": "Bei Iliyokubaliwa",
  "buyer.deal.qty": "Kiasi",
  "buyer.deal.status": "Hali",

  "seller.title": "Dashibodi ya Muuzaji",
  "seller.subtitle": "Simamia orodha, matoleo na miamala",
  "seller.tabs.listings": "Orodha Zangu",
  "seller.tabs.offers": "Matoleo Yaliyopokelewa",
  "seller.tabs.deals": "Miamala Yangu",
  "seller.createListing": "Tengeneza Orodha",
  "seller.empty.listings": "Hakuna orodha. Anza kwa kuongeza moja.",
  "seller.empty.offers": "Hakuna toleo lililopokelewa.",
  "seller.empty.deals": "Hakuna miamala hai.",
  "seller.acceptOffer": "Kubali",
  "seller.counter": "Toa Mbadala",
  "seller.reject": "Kataa",
  "seller.deleteListing": "Futa Orodha",
  "seller.deleteConfirm": "Futa orodha hii? Haiwezi kurejeshwa.",
  "seller.listingDeleted": "Orodha imefutwa",
  "seller.offerAccepted": "Toleo limekubaliwa",
  "seller.offerRejected": "Toleo limekataliwa",
  "seller.unverifiedNotice": "Orodha zako zitawekwa Inasubiri Uthibitisho hadi zitakapokaguliwa na admin.",

  "create.title": "Tengeneza Orodha ya Madini",
  "create.subtitle": "Toa maelezo sahihi kuvutia wanunuzi waliothibitishwa",
  "create.field.title": "Kichwa",
  "create.field.description": "Maelezo",
  "create.field.category": "Aina",
  "create.field.grade": "Daraja / Ubora",
  "create.field.price": "Bei (USD)",
  "create.field.unit": "Kipimo",
  "create.field.quantity": "Kiasi Kilichopo",
  "create.field.location": "Mahali",
  "create.field.images": "Pakia Picha",
  "create.submit": "Chapisha Orodha",
  "create.success": "Orodha imeundwa kwa mafanikio",
  "create.fail": "Imeshindikana kuunda orodha",

  "detail.makeOffer": "Toa Toleo",
  "detail.contactSeller": "Wasiliana na Muuzaji",
  "detail.specs": "Vipimo",
  "detail.description": "Maelezo",
  "detail.seller": "Muuzaji",
  "detail.location": "Mahali",
  "detail.listed": "Imeorodheshwa",
  "detail.views": "Mwonekano",
  "detail.offerPlaceholder": "Bei ya toleo lako (USD)",
  "detail.offerQuantity": "Kiasi (kg)",
  "detail.offerMessage": "Ujumbe kwa muuzaji (si lazima)",
  "detail.submitOffer": "Wasilisha Toleo",
  "detail.offerSent": "Toleo limetumwa kwa mafanikio",
  "detail.unverifiedSeller": "Muuzaji Hajathibitishwa",
  "detail.verifiedSeller": "Muuzaji Aliyethibitishwa",
  "detail.pendingApproval": "Inasubiri Uthibitisho",

  "messages.title": "Ujumbe",
  "messages.empty": "Hakuna ujumbe bado.",
  "messages.placeholder": "Andika ujumbe...",
  "messages.send": "Tuma",

  "inspection.title": "Ukaguzi",
  "inspection.scheduleTitle": "Panga Ukaguzi",
  "inspection.subtitle": "Ratibu ukaguzi wa madini kabla ya malipo",
  "inspection.date": "Tarehe ya Ukaguzi",
  "inspection.notes": "Maelezo",
  "inspection.submit": "Panga Ukaguzi",
  "inspection.scheduled": "Ukaguzi umepangwa",

  "doc.title": "Pakia Hati",
  "doc.subtitle": "Pakia vyeti, uchambuzi na uthibitisho kuthibitisha orodha yako",
  "doc.upload": "Pakia Hati",
  "doc.type": "Aina ya Hati",
  "doc.success": "Hati imepakiwa",

  "admin.title": "Dashibodi ya Msimamizi",
  "admin.subtitle": "Usimamizi wa jukwaa, watumiaji, orodha na miamala",
  "admin.tabs.overview": "Muhtasari",
  "admin.tabs.users": "Watumiaji",
  "admin.tabs.listings": "Orodha",
  "admin.tabs.deals": "Miamala",
  "admin.tabs.compliance": "Ufuasi",
  "admin.tabs.audit": "Kumbukumbu",
  "admin.stats.users": "Watumiaji wote",
  "admin.stats.listings": "Orodha hai",
  "admin.stats.deals": "Miamala hai",
  "admin.stats.gmv": "Mauzo Yote",
  "admin.users.deleteConfirm": "Futa mtumiaji huyu kabisa?",
  "admin.users.deleted": "Mtumiaji amefutwa",
  "admin.listings.approve": "Kubali",
  "admin.listings.reject": "Kataa",
  "admin.listings.approved": "Orodha imekubaliwa",
  "admin.listings.rejected": "Orodha imekataliwa",
  "admin.listings.delete": "Futa",
  "admin.listings.deleteConfirm": "Lazimisha kufuta orodha hii?",
  "admin.deals.verify": "Thibitisha Muamala",
  "admin.deals.verified": "Muamala umethibitishwa na uko mbioni",

  "insights.title": "Maarifa ya Soko",
  "insights.subtitle": "Mahitaji na mwenendo wa madini ya Afrika kwa wakati halisi",

  "how.title": "GlobalMinerX inavyofanya kazi",
  "how.subtitle": "Kutoka orodha hadi utoaji — safari yako",

  "footer.company": "Kuunganisha watoaji wa madini wa Kiafrika na wanunuzi wa kimataifa. Biashara ya Coltan, Shaba, Cobalt na zaidi.",
  "footer.quickLinks": "Viungo vya Haraka",
  "footer.popularMinerals": "Madini Maarufu",
  "footer.connect": "Unganisha Nasi",
  "footer.rights": "Kuunganisha Afrika na Wanunuzi wa Kimataifa. Haki zote zimehifadhiwa.",
  "footer.privacy": "Sera ya Faragha",
  "footer.terms": "Masharti ya Huduma",

  "status.pending": "Inasubiri",
  "status.accepted": "Imekubaliwa",
  "status.rejected": "Imekataliwa",
  "status.countered": "Imejibiwa",
  "status.cancelled": "Imeghairiwa",
  "status.expired": "Imeisha muda",
  "status.in_progress": "Inaendelea",
  "status.completed": "Imekamilika",
  "status.disputed": "Inayobishaniwa",
  "status.verified": "Imethibitishwa",
  "status.unverified": "Hayajathibitishwa",
  "status.pending_verification": "Inasubiri Uthibitisho",
  "status.approved": "Imeidhinishwa",
  "status.under_review": "Inakaguliwa",
  "status.escalated": "Imeongezewa",
  "status.active": "Hai",
  "status.inactive": "Si hai",
  "status.suspended": "Imesimamishwa",
  "status.draft": "Rasimu",
  "status.scheduled": "Imepangwa",
  "status.in_inspection": "Katika Ukaguzi",
  "status.payment_pending": "Malipo Yanasubiri",
  "status.shipped": "Imesafirishwa",
  "status.delivered": "Imefikishwa",

  "error.generic": "Kuna tatizo limetokea",
  "error.network": "Hitilafu ya mtandao. Jaribu tena.",
  "error.unauthorized": "Hujaidhinishwa. Ingia tena.",
};

const zh = {
  "nav.login": "登录",
  "nav.signup": "注册",
  "nav.messages": "消息",
  "nav.dashboard": "仪表板",
  "nav.logout": "登出",
  "nav.backHome": "返回首页",
  "nav.howItWorks": "工作原理",
  "nav.pricing": "定价与费用",

  "hero.tagline": "全球矿产贸易",
  "hero.title": "全球矿产贸易",
  "hero.subtitle": "简化",
  "hero.description": "与来自非洲及其他地区的经过验证的铁矿石、宝石和矿产买卖双方建立联系。托管保护确保交易安全。",
  "hero.getStarted": "开始使用",
  "hero.hotCommodities": "热门商品",

  "hot.banner": "关键需求",
  "hot.title": "最畅销矿产",
  "hot.description": "需求量大的战略矿产。立即上市，获得即时可见性和优质定价。",
  "hot.use": "用途",
  "hot.viewListings": "查看列表",
  "hot.demand.critical": "关键",
  "hot.demand.veryHigh": "非常高",
  "hot.demand.high": "高",
  "hot.use.electronics": "电子产品和智能手机",
  "hot.use.infrastructure": "基础设施和电动汽车",
  "hot.use.evBatteries": "电动汽车电池",
  "hot.use.batteryProduction": "电池生产",
  "hot.use.steel": "钢铁生产",
  "hot.use.smelting": "冶炼和精炼",

  "featured.title": "精选矿产",
  "featured.subtitle": "发现市场上的热门和最新添加的矿产",
  "featured.empty": "尚无矿产上架。成为第一个卖家！",
  "featured.startSelling": "开始销售",
  "featured.viewAll": "查看所有矿产",
  "featured.verified": "已验证",
  "featured.docs": "文件",
  "featured.docsPlural": "文件",
  "featured.by": "卖家",

  "features.title": "为什么选择GlobalMinerX",
  "features.subtitle": "最值得信赖的国际矿产贸易平台",
  "features.escrow.title": "安全托管",
  "features.escrow.desc": "付款安全保管直至交付确认。3%佣金确保优质服务。",
  "features.buyers.title": "直接买家",
  "features.buyers.desc": "直接与制造商和贸易商联系。无中间商，更高利润。",
  "features.insights.title": "市场洞察",
  "features.insights.desc": "查看实时需求数据。在上市前了解买家需求。",

  "cta.title": "准备开始交易？",
  "cta.description": "加入已与全球买家联系的非洲矿产供应商。今天就上架钶钽铁矿、铜、钴。",
  "cta.button": "立即加入 - 免费",

  "auth.welcome": "欢迎回来",
  "auth.create": "创建账户",
  "auth.login": "登录您的账户",
  "auth.join": "加入矿产贸易社区",
  "auth.name": "全名",
  "auth.email": "电子邮件",
  "auth.password": "密码",
  "auth.role": "我想",
  "auth.buyer": "购买矿产",
  "auth.buyerDesc": "浏览和购买矿产",
  "auth.seller": "销售矿产",
  "auth.sellerDesc": "列出并销售您的矿产",
  "auth.admin": "管理员",
  "auth.adminDesc": "平台管理",
  "auth.loginBtn": "登录",
  "auth.createBtn": "创建账户",
  "auth.noAccount": "没有账户？注册",
  "auth.hasAccount": "已有账户？登录",
  "auth.forgot": "忘记密码？",
  "auth.toast.loginSuccess": "登录成功！",
  "auth.toast.verifyEmailHint": "请验证您的邮箱以使用所有功能",
  "auth.toast.regSuccess": "注册成功！请检查您的邮箱。",
  "auth.toast.failed": "认证失败",
  "auth.resetTitle": "重置密码",
  "auth.resetSubtitle": "输入您的邮箱，我们将发送重置说明。",
  "auth.newPassword": "新密码",
  "auth.confirmPassword": "确认密码",
  "auth.sendResetLink": "发送重置链接",
  "auth.resetSuccess": "密码重置邮件已发送，请检查邮箱。",
  "auth.passwordsDontMatch": "密码不匹配",
  "auth.passwordReset": "密码重置成功！正在跳转...",
  "auth.verifyTitle": "验证您的邮箱",
  "auth.verifying": "正在验证邮箱...",
  "auth.verifySuccess": "邮箱验证成功！",
  "auth.verifyFail": "验证失败",
  "auth.continueDashboard": "进入仪表板",
  "auth.resendEmail": "重新发送验证邮件",
  "auth.resendSent": "验证邮件已发送",

  "common.loading": "加载中...",
  "common.search": "搜索",
  "common.filter": "筛选",
  "common.browse": "浏览矿产",
  "common.cancel": "取消",
  "common.save": "保存",
  "common.submit": "提交",
  "common.confirm": "确认",
  "common.delete": "删除",
  "common.edit": "编辑",
  "common.view": "查看",
  "common.close": "关闭",
  "common.back": "返回",
  "common.next": "下一步",
  "common.yes": "是",
  "common.no": "否",
  "common.price": "价格",
  "common.quantity": "数量",
  "common.location": "位置",
  "common.category": "类别",
  "common.grade": "等级/质量",
  "common.actions": "操作",
  "common.status": "状态",
  "common.date": "日期",
  "common.all": "全部",
  "common.none": "无",
  "common.export": "导出",
  "common.refresh": "刷新",
  "common.success": "成功",
  "common.error": "错误",
  "common.unverified": "未验证",
  "common.verified": "已验证",
  "common.pending": "待处理",

  "minerals.coltan": "钶钽铁矿 (钽铁矿)",
  "minerals.copper": "铜精矿",
  "minerals.copperCathode": "铜阴极",
  "minerals.cobalt": "钴矿石",
  "minerals.lithium": "锂矿石",
  "minerals.manganese": "锰",
  "minerals.chromium": "铬",
  "minerals.gold": "黄金",
  "minerals.tin": "锡",
  "minerals.tungsten": "钨",
  "minerals.iron": "铁矿石",
  "minerals.diamond": "钻石",
  "minerals.gemstone": "宝石",
  "minerals.other": "其他",

  "buyer.title": "买家仪表板",
  "buyer.subtitle": "发现优质矿产并跟踪您的交易",
  "buyer.tabs.marketplace": "市场",
  "buyer.tabs.myOffers": "我的报价",
  "buyer.tabs.myDeals": "我的交易",
  "buyer.searchPlaceholder": "搜索矿产、地点...",
  "buyer.filterCategory": "类别",
  "buyer.allCategories": "所有类别",
  "buyer.empty.minerals": "未找到矿产。请更改筛选条件。",
  "buyer.empty.offers": "您尚未提交任何报价。",
  "buyer.empty.deals": "暂无交易。",
  "buyer.makeOffer": "出价",
  "buyer.viewDetails": "查看详情",
  "buyer.cancelOffer": "取消报价",
  "buyer.cancelOfferConfirm": "取消此报价？此操作无法撤消。",
  "buyer.offerCancelled": "报价已取消",
  "buyer.viewSummary": "查看交易摘要",
  "buyer.offerAmount": "您的报价",
  "buyer.targetPrice": "标价",
  "buyer.deal.id": "交易ID",
  "buyer.deal.agreed": "约定价格",
  "buyer.deal.qty": "数量",
  "buyer.deal.status": "状态",

  "seller.title": "卖家仪表板",
  "seller.subtitle": "管理上架、报价和交易",
  "seller.tabs.listings": "我的上架",
  "seller.tabs.offers": "收到的报价",
  "seller.tabs.deals": "我的交易",
  "seller.createListing": "创建上架",
  "seller.empty.listings": "暂无上架。开始创建一个。",
  "seller.empty.offers": "尚未收到报价。",
  "seller.empty.deals": "暂无活跃交易。",
  "seller.acceptOffer": "接受",
  "seller.counter": "还价",
  "seller.reject": "拒绝",
  "seller.deleteListing": "删除上架",
  "seller.deleteConfirm": "删除此上架？此操作无法撤消。",
  "seller.listingDeleted": "上架已删除",
  "seller.offerAccepted": "报价已接受",
  "seller.offerRejected": "报价已拒绝",
  "seller.unverifiedNotice": "您的上架将标记为待验证，直到管理员审核通过。",

  "create.title": "创建矿产上架",
  "create.subtitle": "提供准确详情以吸引经过验证的买家",
  "create.field.title": "标题",
  "create.field.description": "描述",
  "create.field.category": "类别",
  "create.field.grade": "等级 / 质量",
  "create.field.price": "价格 (美元)",
  "create.field.unit": "单位",
  "create.field.quantity": "可用数量",
  "create.field.location": "位置",
  "create.field.images": "上传图片",
  "create.submit": "发布上架",
  "create.success": "上架创建成功",
  "create.fail": "创建上架失败",

  "detail.makeOffer": "出价",
  "detail.contactSeller": "联系卖家",
  "detail.specs": "规格",
  "detail.description": "描述",
  "detail.seller": "卖家",
  "detail.location": "位置",
  "detail.listed": "已上架",
  "detail.views": "浏览量",
  "detail.offerPlaceholder": "您的报价 (美元)",
  "detail.offerQuantity": "数量 (公斤)",
  "detail.offerMessage": "给卖家的留言 (可选)",
  "detail.submitOffer": "提交报价",
  "detail.offerSent": "报价已成功发送",
  "detail.unverifiedSeller": "未验证的卖家",
  "detail.verifiedSeller": "已验证的卖家",
  "detail.pendingApproval": "待验证",

  "messages.title": "消息",
  "messages.empty": "暂无消息。",
  "messages.placeholder": "输入消息...",
  "messages.send": "发送",

  "inspection.title": "检查",
  "inspection.scheduleTitle": "安排检查",
  "inspection.subtitle": "在付款释放前协调矿石批次的检查",
  "inspection.date": "检查日期",
  "inspection.notes": "备注",
  "inspection.submit": "安排检查",
  "inspection.scheduled": "检查已安排",

  "doc.title": "文档上传",
  "doc.subtitle": "上传证书、检测和证明以验证您的上架",
  "doc.upload": "上传文档",
  "doc.type": "文档类型",
  "doc.success": "文档已上传",

  "admin.title": "管理员仪表板",
  "admin.subtitle": "平台治理、用户、上架和交易",
  "admin.tabs.overview": "概览",
  "admin.tabs.users": "用户",
  "admin.tabs.listings": "上架",
  "admin.tabs.deals": "交易",
  "admin.tabs.compliance": "合规",
  "admin.tabs.audit": "审计日志",
  "admin.stats.users": "用户总数",
  "admin.stats.listings": "活跃上架",
  "admin.stats.deals": "活跃交易",
  "admin.stats.gmv": "总交易额",
  "admin.users.deleteConfirm": "永久删除此用户？",
  "admin.users.deleted": "用户已删除",
  "admin.listings.approve": "批准",
  "admin.listings.reject": "拒绝",
  "admin.listings.approved": "上架已批准",
  "admin.listings.rejected": "上架已拒绝",
  "admin.listings.delete": "删除",
  "admin.listings.deleteConfirm": "强制删除此上架？",
  "admin.deals.verify": "验证交易",
  "admin.deals.verified": "交易已验证并进入进行中",

  "insights.title": "市场洞察",
  "insights.subtitle": "非洲矿产的实时需求和趋势",

  "how.title": "GlobalMinerX 工作原理",
  "how.subtitle": "从上架到交付——您的旅程",

  "footer.company": "连接非洲矿产供应商与全球买家。贸易钽铁矿、铜、钴等。",
  "footer.quickLinks": "快速链接",
  "footer.popularMinerals": "热门矿产",
  "footer.connect": "联系我们",
  "footer.rights": "连接非洲与全球买家。版权所有。",
  "footer.privacy": "隐私政策",
  "footer.terms": "服务条款",

  "status.pending": "待处理",
  "status.accepted": "已接受",
  "status.rejected": "已拒绝",
  "status.countered": "已还价",
  "status.cancelled": "已取消",
  "status.expired": "已过期",
  "status.in_progress": "进行中",
  "status.completed": "已完成",
  "status.disputed": "有争议",
  "status.verified": "已验证",
  "status.unverified": "未验证",
  "status.pending_verification": "待验证",
  "status.approved": "已批准",
  "status.under_review": "审核中",
  "status.escalated": "已升级",
  "status.active": "活跃",
  "status.inactive": "非活跃",
  "status.suspended": "已暂停",
  "status.draft": "草稿",
  "status.scheduled": "已安排",
  "status.in_inspection": "检查中",
  "status.payment_pending": "待付款",
  "status.shipped": "已发货",
  "status.delivered": "已送达",

  "error.generic": "出错了",
  "error.network": "网络错误。请重试。",
  "error.unauthorized": "未授权。请重新登录。",
};

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  sw: { translation: sw },
  zh: { translation: zh },
};

const initialLng = (() => {
  try {
    // Read from URL ?lng= first (highest priority), then localStorage
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('lng');
    if (fromUrl && ['en', 'fr', 'sw', 'zh'].includes(fromUrl)) return fromUrl;
    const stored = localStorage.getItem('i18nextLng');
    if (stored) {
      const code = stored.split('-')[0];
      if (['en', 'fr', 'sw', 'zh'].includes(code)) return code;
    }
  } catch {}
  return undefined; // let LanguageDetector handle navigator fallback
})();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ...(initialLng ? { lng: initialLng } : {}),
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'sw', 'zh'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'i18nextLng',
    },
    react: { useSuspense: false },
  });

/**
 * Translate a backend status enum value (e.g. "pending_verification") to the
 * user's currently selected language. Falls back to a humanized version of the
 * raw value when no translation is available.
 */
export function tStatus(value) {
  if (value === null || value === undefined) return '';
  const normalized = String(value).toLowerCase().replace(/\s+/g, '_');
  const key = `status.${normalized}`;
  const translated = i18n.t(key);
  if (translated !== key) return translated;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Translate a category enum value to a localized mineral name where possible.
 */
export function tCategory(value) {
  if (!value) return '';
  const map = {
    coltan: 'minerals.coltan',
    tantalite: 'minerals.coltan',
    copper: 'minerals.copper',
    copper_concentrate: 'minerals.copper',
    copper_cathode: 'minerals.copperCathode',
    cobalt: 'minerals.cobalt',
    lithium: 'minerals.lithium',
    manganese: 'minerals.manganese',
    chromium: 'minerals.chromium',
    gold: 'minerals.gold',
    tin: 'minerals.tin',
    tungsten: 'minerals.tungsten',
    iron: 'minerals.iron',
    iron_ore: 'minerals.iron',
    diamond: 'minerals.diamond',
    gemstone: 'minerals.gemstone',
    other: 'minerals.other',
  };
  const normalized = String(value).toLowerCase().replace(/\s+/g, '_');
  const key = map[normalized];
  if (key) {
    const translated = i18n.t(key);
    if (translated !== key) return translated;
  }
  return String(value).replace(/_/g, ' ').toUpperCase();
}

export default i18n;
